import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';
import { useState, useEffect, useRef } from 'react';

import useQuery from 'hooks/useQuery';
import useMutation from 'hooks/useMutation';
import ResourceSelector from './ResourceSelector';

const EHRVisibility = (props) => {
  const { t } = useTranslation();
  const resourceVisibility = props.type === 'record' ? props.resourceVisibility : props.visibility;


  const resources = useQuery(window._l("/resources").substr(1) + "?deleted=true");

  // Determinar la visibilidad efectiva: si es 'default', usar la del recurso, sino la del registro
  const getEffectiveVisibility = () => {
    if (props.visibility?.type === 'default') {
      return resourceVisibility || { type: 'public' };
    }
    return props.visibility || { type: 'public' };
  };

  const visibility = getEffectiveVisibility();

  const onChangeVisibility = (e) => {
    const newConfig = {
      ...visibility,
      type: e.target.name,
    };
    if(props.type === 'record' && e.target.name === resourceVisibility?.type) {
      newConfig.type = 'default';
    }

    if (props.onChange) {
      props.onChange(newConfig);
    }
  };

  // Bug real (auditoría 2026-08-16): onChangeResources quedó como código
  // muerto — ambas instancias de ResourceSelector (whitelist/blacklist) usan
  // sus propios onChange/onMenuOpen inline en vez de esta función, así que
  // no tenía ningún call site en el archivo. Confirmado con
  // `grep -n "onChangeResources" EHRVisibility.js` antes de borrarla.

  const handleClearSelection = () => {
    // Borrar la configuración específica del registro y usar la del recurso
    if (props.onChange) {
      props.onChange({ type: 'default' });
    }
  };

  return (
    <div>
      <div className="row">
        <div className="col-12">
          <div className="row">
            <div className="col-12 mb-3">
              <span dangerouslySetInnerHTML={{
                __html: props.type === 'record' ? t('ehr:visibility_config_instructions_record',
                  `Elegí la visibilidad de este registro. Podés usar la configuracion predeterminada o modificarla.`
                ) : t('ehr:visibility_config_instructions',
                  `Elegí la visibilidad predeterminada de tus regsitros en las historias clínicas de los pacientes, ` +
                  `<strong>Este cambio se aplicará también de forma retroactiva a tus registros anteriores, y siempre vas a poder modificarla en cada registro puntual.</strong>`
                )
              }} />
            </div>
            <div className="col-12">
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="radio"
                  name="public"
                  id="visibility-public"
                  checked={visibility?.type === 'public'}
                  onChange={onChangeVisibility}
                />
                <label className="form-check-label" htmlFor="visibility-public">
                  {t('ehr:visibility_config_public', 'Público (Todos los profesionales de la cuenta)')}
                </label>
                {props.type === 'record' && (resourceVisibility?.type === 'public' || !resourceVisibility) && (
                  <span className="badge bg-light text-secondary ms-1" style={{ fontSize: '0.7rem' }}>
                    {t('ehr:default_visibility', 'Predeterminada')}
                  </span>
                )}
              </div>
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="radio"
                  name="private"
                  id="visibility-private"
                  checked={visibility?.type === 'private'}
                  onChange={onChangeVisibility}
                />
                <label className="form-check-label" htmlFor="visibility-private">
                  {t('ehr:visibility_config_private', 'Privado (Solo yo)')}
                </label>
                {props.type === 'record' && (resourceVisibility?.type === 'private') && (
                  <span className="badge bg-light text-secondary ms-1" style={{ fontSize: '0.7rem' }}>
                    {t('ehr:default_visibility', 'Predeterminada')}
                  </span>
                )}
              </div>
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="radio"
                  name="custom"
                  id="visibility-custom"
                  checked={visibility?.type === 'whitelist' || visibility?.type === 'blacklist'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const newConfig = {
                        ...visibility,
                        type: 'whitelist',
                        whitelist: {},
                        blacklist: {}
                      };
                      if (props.onChange) {
                        props.onChange(newConfig);
                      }
                    }
                  }}
                />
                <label className="form-check-label" htmlFor="visibility-custom">
                  {t('ehr:visibility_config_custom', 'Personalizado (Seleccionar profesionales)')}
                </label>
                {props.type === 'record' && (resourceVisibility?.type === 'blacklist' || resourceVisibility?.type === 'whitelist') && (
                  <span className="badge bg-light text-secondary ms-1" style={{ fontSize: '0.7rem' }}>
                    {t('ehr:default_visibility', 'Predeterminada')}
                  </span>
                )}

                {(visibility?.type === 'whitelist' || visibility?.type === 'blacklist') && (
                  <div className="ms-4 mt-2">
                    <div className="form-check mb-2 d-flex flex-column flex-md-row align-items-start align-items-md-center">
                      <div className="d-flex align-items-center mb-2 mb-md-0">
                      <input
                        className="form-check-input me-2"
                        type="radio"
                        name="custom-type"
                        id="custom-whitelist"
                        checked={visibility?.type === 'whitelist'}
                        onChange={() => {
                          const newConfig = {
                            ...visibility,
                            type: 'whitelist'
                          };
                          if (props.onChange) {
                            props.onChange(newConfig);
                          }
                        }}
                      />
                      <label className="form-check-label me-3" htmlFor="custom-whitelist">
                        {t('ehr:visibility_config_visible_for', 'Visible para:')}
                      </label>
                      </div>
                      {/* Bug real (auditoría 2026-08-18, responsive): sin
                          flex-wrap, el ResourceSelector (minWidth fijo, ver
                          ResourceSelector.js) + el botón "Borrar nueva
                          selección" (texto largo, caso alcanzable: un
                          registro puntual con override de visibilidad ya
                          activo) desbordan los ~280px disponibles dentro de
                          este modal (modal-lg sin modal-fullscreen-sm-down)
                          a 375px -- el overflow-x:hidden global puede tapar
                          el único botón para revertir el override. */}
                      <div className="flex-grow-1 d-flex align-items-center flex-wrap w-100 gap-2">
                        <ResourceSelector
                          resources={resources.data}
                          selectedResources={(() => {
                            const effectiveVisibility = props.visibility?.type === 'default' && resourceVisibility ? resourceVisibility : visibility;
                            return effectiveVisibility?.whitelist || {};
                          })()}
                          onChange={(updatedResources) => {
                            const newType = props.visibility?.type === 'default' ? 'whitelist' : visibility.type;
                            const newConfig = {
                              ...visibility,
                              type: newType,
                              whitelist: updatedResources
                            };

                            if (props.onChange) {
                              props.onChange(newConfig);
                            }
                          }}
                          onMenuOpen={() => {
                            // Cambiar automáticamente al tipo whitelist
                            const newConfig = {
                              ...visibility,
                              type: 'whitelist',
                              blacklist: {}
                            };
                            if (props.onChange) {
                              props.onChange(newConfig);
                            }
                          }}
                          placeholder={t('ehr:visibility_config_search_professionals', 'Buscar profesionales')}
                          excludeResourceId={props.resource?.id}
                        />
                        {props.type === 'record'
                          && (visibility?.type === 'whitelist')
                          && (props.visibility?.type !== 'default') && (
                            <button
                              type="button"
                              className="btn btn-sm btn-link ms-2"
                              onClick={handleClearSelection}
                              title={t('ehr:clear_visibility_selection_title', 'Borrar selección y usar configuración predeterminada')}
                            >
                              {t('ehr:clear_visibility_selection', 'Borrar nueva selección')}
                            </button>
                          )}
                      </div>
                    </div>
                    <div className="form-check mb-2 d-flex flex-column flex-md-row align-items-start align-items-md-center">
                      <div className="d-flex align-items-center mb-2 mb-md-0">
                      <input
                        className="form-check-input me-2"
                        type="radio"
                        name="custom-type"
                        id="custom-blacklist"
                        checked={visibility?.type === 'blacklist'}
                        onChange={() => {
                          const newConfig = {
                            ...visibility,
                            type: 'blacklist',
                            whitelist: {}
                          };
                          if (props.onChange) {
                            props.onChange(newConfig);
                          }
                        }}
                      />
                      <label className="form-check-label me-3" htmlFor="custom-blacklist">
                        {t('ehr:visibility_config_hidden_for', 'Oculto para:')}
                      </label>
                      </div>
                      {/* Bug real (auditoría 2026-08-18, responsive): sin
                          flex-wrap, el ResourceSelector (minWidth fijo, ver
                          ResourceSelector.js) + el botón "Borrar nueva
                          selección" (texto largo, caso alcanzable: un
                          registro puntual con override de visibilidad ya
                          activo) desbordan los ~280px disponibles dentro de
                          este modal (modal-lg sin modal-fullscreen-sm-down)
                          a 375px -- el overflow-x:hidden global puede tapar
                          el único botón para revertir el override. */}
                      <div className="flex-grow-1 d-flex align-items-center flex-wrap w-100 gap-2">
                        <ResourceSelector
                          resources={resources.data}
                          selectedResources={(() => {
                            const effectiveVisibility = props.visibility?.type === 'default' && resourceVisibility ? resourceVisibility : visibility;
                            return effectiveVisibility?.blacklist || {};
                          })()}
                          onChange={(updatedResources) => {
                            const newType = props.visibility?.type === 'default' ? 'blacklist' : visibility.type;
                            const newConfig = {
                              ...visibility,
                              type: newType,
                              blacklist: updatedResources
                            };

                            if (props.onChange) {
                              props.onChange(newConfig);
                            }
                          }}
                          onMenuOpen={() => {
                            // Cambiar automáticamente al tipo blacklist
                            const newConfig = {
                              ...visibility,
                              type: 'blacklist',
                              whitelist: {}
                            };
                            if (props.onChange) {
                              props.onChange(newConfig);
                            }
                          }}
                          placeholder={t('ehr:visibility_config_search_professionals', 'Buscar profesionales')}
                          excludeResourceId={props.resource?.id}
                        />
                        {props.type === 'record'
                          && (visibility?.type === 'blacklist')
                          && (props.visibility?.type !== 'default') && (
                            <button
                              type="button"
                              className="btn btn-sm btn-link ms-2"
                              onClick={handleClearSelection}
                              title={t('ehr:clear_visibility_selection_title', 'Borrar selección y usar configuración predeterminada')}
                            >
                              {t('ehr:clear_visibility_selection', 'Borrar nueva selección')}
                            </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>


        </div>
      </div>
    </div>
  );
};

export default EHRVisibility;